/**
 * @file PaddleHub 上传模型浮窗
 * @author FengGuang(fengguang01@baidu.com)
 */


import React, {useCallback, Fragment} from 'react';
import {Button, Form, Input, Modal, Select, Tooltip, Upload, UploadProps} from 'antd';
import {QuestionCircleOutlined} from '@ant-design/icons';
import {FormInstance} from 'antd/es/form';

import {uploadToBos} from '../../../../api/pages/paddleHubBosApi';
import UploadBosClient from '../../../../utils/bos-client';
import {generateShouldUpdate} from '../../../../utils/antd-form-should-update';

export interface IUploadModelModalProps {
    modelCategoryList: string[];
    visible: boolean;
    onOk: () => void;
    onOkSubmit: (value: any) => void;
    onCancel: () => void;
    form: FormInstance
}

const UploadModelModal: React.FC<IUploadModelModalProps> = props => {
    const {
        modelCategoryList,
        visible,
        onCancel,
        onOkSubmit,
        form
    } = props;

    const uploadCustomRequest = useCallback<Exclude<UploadProps['customRequest'], undefined>>(
        (options) => {
            const {
                file,
                onError,
                onProgress,
                onSuccess
            } = options;

            let client: UploadBosClient | undefined = undefined;
            (async () => {
                try {
                    client = await uploadToBos({
                        // antd 4.13.1 类型错误
                        file: options.file as File,
                        // antd 的类型有问题
                        // @ts-ignore
                        onProgress: percent => onProgress && onProgress({percent})
                    });
                }
                catch (err: any) {
                    onError && onError(err, file);
                }
                if (client) {
                    const res = await client.start();
                    // antd 4.13.1 的类型有问题
                    // @ts-ignore
                    onSuccess && onSuccess(res, file);
                }
            })();
            return {
                abort() {
                    if (client) {
                        client.abort();
                    }
                }
            };
        },
        []
    );

    return (
        <Modal
            className="paddle-hub-upload-model-modal"
            title="上传模型"
            visible={visible}
            onOk={form.submit}
            onCancel={onCancel}
            footer={<Fragment>
                <Button onClick={onCancel}>取消</Button>
                <Button
                    type="primary"
                    onClick={form.submit}
                >
                    提交
                </Button>
            </Fragment>}
        >
            <Form
                form={form}
                onFinish={onOkSubmit}
                layout="vertical"
            >
                <Form.Item
                    label="模型类型"
                    name="modelCategory"
                    initialValue="文本"
                    colon={false}
                    required={false}
                >
                    <Select
                        dropdownClassName="paddle-hub-upload-model-modal-select"
                        size="large"
                    >
                        {modelCategoryList.map(item => (
                            <Select.Option key={item} value={item}>{item}</Select.Option>
                        ))}
                    </Select>
                </Form.Item>
                <Form.Item
                    label="模型名称"
                    name="modelName"
                    colon={false}
                    required={false}
                    rules={[{required: true, message: ''}]}
                >
                    <Input.TextArea
                        rows={3}
                        placeholder="名称仅限字母、数字和“_”，如: shufflenet_v2_imagenet (50字以内）"
                    />
                </Form.Item>
                <Form.Item
                    label="模型介绍"
                    name="modelAbs"
                    colon={false}
                    required={false}
                    rules={[{required: true, message: ''}]}
                >
                    <Input.TextArea
                        rows={3}
                        placeholder="请输入模型介绍（限200字以内）"
                    />
                </Form.Item>
                <Form.Item
                    label="邮箱地址"
                    name="userEmail"
                    colon={false}
                    required={false}
                    rules={[{required: true, message: '', type: 'email'}]}
                >
                    <Input
                        size="large"
                        placeholder="提交模型5-7个工作日内，我们会将审核意见发到您邮箱"
                    />
                </Form.Item>
                <Form.Item
                    label={
                        <span>
                            模型上传
                            <Tooltip
                                placement="right"
                                title={
                                    <a
                                        className="paddle-hub-upload-model-modal-upload-tooltip-link"
                                        href="https://github.com/PaddlePaddle/PaddleHub/wiki/%E5%A6%82%E4%BD%95%E8%B4%A1%E7%8C%AEModule"
                                    >
                                        如何贡献Moudle？
                                    </a>
                                }
                            >
                                <QuestionCircleOutlined
                                    className="paddle-hub-upload-model-modal-upload-tooltip-icon"
                                />
                            </Tooltip>
                            <span className="paddle-hub-upload-model-modal-upload-sublabel">
                                仅支持.zip和.tar格式的压缩文件
                            </span>
                        </span>
                    }
                    name="modelFileList"
                    colon={false}
                    required={false}
                    rules={[{required: true, message: '请上传文件', type: 'array'}]}
                    valuePropName="fileList"
                    getValueFromEvent={(event) => {
                        let fileList = event.fileList || [];
                        if (event.file.status === 'done') {
                            fileList = fileList.slice(-1);
                            const theFile = fileList[0];
                            theFile.url = (theFile.response?.body || {}).location;
                        }
                        return fileList;
                    }}
                    shouldUpdate={generateShouldUpdate(['modelFileList'])}
                >
                    <Upload
                        customRequest={uploadCustomRequest}
                        accept=".zip,.tar"
                    >
                        <Form.Item
                            noStyle
                            shouldUpdate={generateShouldUpdate(['modelFileList'])}
                        >
                            {({getFieldsError}) => (
                                <Button
                                    className="paddle-hub-upload-model-modal-upload-btn"
                                    type="primary"
                                    ghost
                                    danger={getFieldsError().filter(i => i.errors.length > 0).length > 0}
                                >
                                    上传模型
                                </Button>
                            )}
                        </Form.Item>
                    </Upload>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default UploadModelModal;
